import { Link } from 'react-router-dom';
import { RecordConfigSettings } from './RecordConfigSettings';
import { useRecordConfig } from './recordHooks';
import { usePageTitle } from '../../hooks/usePageTitle';

/**
 * Dedicated page for configuring the per-tenant customer-record type. Leads with
 * a plain-language explanation of how the pieces relate — the configurable
 * service label, the per-customer records under it, and the fields captured on
 * each — before the editor itself. Reached from Settings → Customer records.
 */
export function RecordConfigPage() {
  usePageTitle('Client records');
  const { data: config } = useRecordConfig(true);
  const singular = config?.singularLabel ?? 'record';
  const plural = config?.pluralLabel ?? 'records';

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
      <div>
        <Link to="/settings" className="text-sm text-slate-500 hover:text-sky-600">
          ← Settings
        </Link>
        <h1 className="text-2xl font-semibold text-slate-800 mt-2">Client records</h1>
      </div>

      <div className="bg-sky-50 border border-sky-100 rounded-xl p-6 space-y-4 text-sm text-slate-700">
        <h2 className="text-base font-semibold text-slate-800">How this works</h2>
        <p>
          You choose a <strong>service label</strong> for the thing you keep on file for each
          customer. A pet sitter calls them “Pets”; a house sitter might call them “Houses”. Yours is
          currently <strong>{plural}</strong>.
        </p>
        <p>
          The <strong>fields</strong> you define below are captured for every {singular}. When you
          open a customer, you add their {plural} and fill in these fields for each one — so the
          fields here shape the data stored under every customer’s {plural}.
        </p>
        <p>
          The <strong>primary field</strong> names each {singular}: it’s what appears in customer
          lists, on visits, and in the <code>{'{{RecordNames}}'}</code> document token.
        </p>

        {/* Concrete relationship sketch using the live labels. */}
        <div className="rounded-lg border border-slate-200 bg-white p-4 leading-relaxed">
          <div className="font-medium text-slate-800">A customer</div>
          <div className="ml-3 mt-1 border-l-2 border-sky-200 pl-3">
            <div>
              has one or more <strong>{plural}</strong>{' '}
              <span className="text-xs text-slate-400">(your service label)</span>
            </div>
            <div className="ml-3 mt-1 border-l-2 border-slate-200 pl-3 text-slate-600">
              each <strong>{singular}</strong> stores the fields you define below
            </div>
          </div>
        </div>
      </div>

      <RecordConfigSettings />
    </div>
  );
}
